import { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AppLayout from '../components/common/Layout';
import { teamApi } from '../api/teamApi';
import { useApi } from '../hooks/useApi';

const { Title, Text } = Typography;

const Team = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const { loading: submitLoading, execute } = useApi();

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await teamApi.getAll();
      setMembers(response.data.members);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ position: 'Frontend Developer' });
    setModalOpen(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    form.setFieldsValue(member);
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    if (editing) {
      await execute(() => teamApi.update(editing._id, values), {
        successMessage: 'Сотрудник обновлён',
        onSuccess: () => {
          setModalOpen(false);
          fetchMembers();
        },
      });
    } else {
      await execute(() => teamApi.create(values), {
        successMessage: 'Сотрудник добавлен',
        onSuccess: () => {
          setModalOpen(false);
          fetchMembers();
        },
      });
    }
  };

  const handleDelete = async (id) => {
    await execute(() => teamApi.delete(id), {
      successMessage: 'Сотрудник удалён',
      onSuccess: fetchMembers,
    });
  };

  const columns = [
    { title: 'Имя', dataIndex: 'fullName', key: 'fullName', sorter: (a, b) => a.fullName.localeCompare(b.fullName) },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v) => v || '—' },
    { title: 'Должность', dataIndex: 'position', key: 'position' },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Редактировать
          </Button>
          <Popconfirm title="Удалить сотрудника?" onConfirm={() => handleDelete(record._id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3}>Команда</Title>
          <Text type="secondary">Сотрудники для One-to-One отчётов</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Добавить
        </Button>
      </div>

      <Table columns={columns} dataSource={members} rowKey="_id" loading={loading} pagination={{ pageSize: 10 }} />

      <Modal
        title={editing ? 'Редактировать сотрудника' : 'Новый сотрудник'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="fullName" label="Полное имя" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="position" label="Должность">
            <Input />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editing ? 'Сохранить' : 'Добавить'}
              </Button>
              <Button onClick={() => setModalOpen(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
};

export default Team;
